import { Box, HStack, Text } from '@chakra-ui/react'
import { useReactFlow } from '@xyflow/react'
import { ChevronDown, ChevronRight, Copy, RefreshCw, Trash2, Ungroup } from 'lucide-react'
import React, { useCallback, useState } from 'react'
import type { PlaygroundNodeMenuItem, PlaygroundNodesMenuResponse } from '@/lib/api'
import { useColorModeValue } from '~/color-mode'

interface ContextMenuProps {
  id: string
  top?: number
  left?: number
  right?: number
  bottom?: number
  onClose?: () => void
  menuData?: PlaygroundNodesMenuResponse
  onChangeNodeType?: (nodeId: string, newNodeData: PlaygroundNodeMenuItem) => void
  onUngroup?: (groupId: string) => void
  [key: string]: any
}

export default function ContextMenu({
  id,
  top,
  left,
  right,
  bottom,
  onClose,
  menuData,
  onChangeNodeType,
  onUngroup,
  ...props
}: ContextMenuProps) {
  const { getNode, getNodes, setNodes, addNodes, setEdges } = useReactFlow()
  const [showChangeType, setShowChangeType] = useState(false)
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)

  // Chrome colors (menu chip UI, not diagram data) - theme-aware for dark mode
  const menuActiveBg = useColorModeValue('#E0DFDF', '#5F5D5A')
  const menuHoverBg = useColorModeValue('#F0F0EF', '#4A4743')
  const menuTextColor = useColorModeValue('#5F5D5A', '#CDCCC9')
  const dangerTextColor = useColorModeValue('#DE2C32', '#FD948B')
  const dangerHoverBg = useColorModeValue('#FDECE8', '#601F1D')

  // Check if the current node is a group node
  const node = getNode(id)
  const isGroupNode = node?.type === 'groupNode'

  const duplicateNode = useCallback(() => {
    const node = getNode(id)
    if (!node) return

    const position = {
      x: node.position.x + 50,
      y: node.position.y + 50,
    }

    addNodes({
      ...node,
      selected: false,
      dragging: false,
      id: `${node.id}-copy`,
      position,
      data: node.data || {},
    })
    onClose?.()
  }, [id, getNode, addNodes, onClose])

  const deleteNode = useCallback(() => {
    setNodes((nodes) => nodes.filter((node) => node.id !== id))
    setEdges((edges) => edges.filter((edge) => edge.source !== id || edge.target !== id))
    onClose?.()
  }, [id, setNodes, setEdges, onClose])

  // Ungroup - removes the group but keeps child nodes
  const ungroupNodes = useCallback(() => {
    const allNodes = getNodes()
    const groupNode = allNodes.find((n) => n.id === id)
    if (!groupNode) return

    // Find all child nodes
    const childNodes = allNodes.filter((n) => n.parentId === id)

    setNodes((nodes) => {
      return nodes
        .filter((n) => n.id !== id) // Remove the group
        .map((n) => {
          // Update children to have absolute positions
          if (n.parentId === id) {
            return {
              ...n,
              parentId: undefined,
              extent: undefined,
              position: {
                x: groupNode.position.x + n.position.x,
                y: groupNode.position.y + n.position.y,
              },
            }
          }
          return n
        })
    })

    onClose?.()
  }, [id, getNodes, setNodes, onClose])

  // Delete group and all children
  const deleteGroupWithChildren = useCallback(() => {
    const allNodes = getNodes()
    // Find all descendants (including nested groups)
    const getDescendantIds = (nodeId: string): string[] => {
      const children = allNodes.filter((n) => n.parentId === nodeId)
      const childIds = children.map((c) => c.id)
      const grandchildIds = children.flatMap((c) => getDescendantIds(c.id))
      return [...childIds, ...grandchildIds]
    }

    const idsToDelete = new Set([id, ...getDescendantIds(id)])

    setNodes((nodes) => nodes.filter((n) => !idsToDelete.has(n.id)))
    setEdges((edges) =>
      edges.filter((e) => !idsToDelete.has(e.source) && !idsToDelete.has(e.target)),
    )
    onClose?.()
  }, [id, getNodes, setNodes, setEdges, onClose])

  return (
    <>
      <Box
        position="absolute"
        style={{ top, left, right, bottom }}
        bg="bg.panel"
        borderRadius="8px"
        boxShadow="0 4px 12px rgba(0,0,0,0.15)"
        p={2}
        zIndex={10000}
        minW="150px"
        {...props}
      >
        {/* Header */}
        <Box fontSize="xs" color="content.secondary" mb={1.5} px={2} fontWeight="medium">
          {isGroupNode ? 'Group' : 'Node'}: {id}
        </Box>

        {/* Divider */}
        <Box borderTopWidth="1px" borderColor="gray.200" my={1.5} />

        {/* Regular node options */}
        {!isGroupNode && (
          <>
            {/* Duplicate button */}
            <button
              type="button"
              onClick={duplicateNode}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 8px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '13px',
                color: menuTextColor,
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = menuHoverBg)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <Copy size={14} />
              Duplicate
            </button>

            {/* Change Type button */}
            {menuData && onChangeNodeType && (
              <button
                type="button"
                onClick={() => setShowChangeType(!showChangeType)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: showChangeType ? menuHoverBg : 'transparent',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: menuTextColor,
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!showChangeType) e.currentTarget.style.backgroundColor = menuHoverBg
                }}
                onMouseLeave={(e) => {
                  if (!showChangeType) e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <HStack gap={2}>
                  <RefreshCw size={14} />
                  <span>Change Type</span>
                </HStack>
                <ChevronRight size={14} />
              </button>
            )}

            {/* Divider */}
            <Box borderTopWidth="1px" borderColor="gray.200" my={1.5} />

            {/* Delete button */}
            <button
              type="button"
              onClick={deleteNode}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 8px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '13px',
                color: dangerTextColor,
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dangerHoverBg)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <Trash2 size={14} />
              Delete
            </button>
          </>
        )}

        {/* Group node options */}
        {isGroupNode && (
          <>
            {/* Ungroup button */}
            <button
              type="button"
              onClick={ungroupNodes}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 8px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '13px',
                color: menuTextColor,
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = menuHoverBg)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <Ungroup size={14} />
              Ungroup
            </button>

            {/* Divider */}
            <Box borderTopWidth="1px" borderColor="gray.200" my={1.5} />

            {/* Delete group only */}
            <button
              type="button"
              onClick={ungroupNodes}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 8px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '13px',
                color: dangerTextColor,
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dangerHoverBg)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <Trash2 size={14} />
              Delete Group Only
            </button>

            {/* Delete group and children */}
            <button
              type="button"
              onClick={deleteGroupWithChildren}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 8px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '13px',
                color: dangerTextColor,
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dangerHoverBg)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <Trash2 size={14} />
              Delete Group + Contents
            </button>
          </>
        )}
      </Box>

      {/* Change Type Flyout Panel */}
      {showChangeType && menuData && onChangeNodeType && !isGroupNode && (
        <Box
          position="absolute"
          style={{ top, left: (left || 0) + 160 }}
          bg="bg.panel"
          borderRadius="8px"
          boxShadow="0 4px 12px rgba(0,0,0,0.15)"
          p={2}
          zIndex={10001}
          minW="180px"
          maxH="350px"
          overflowY="auto"
        >
          {/* Header */}
          <Box fontSize="xs" color="content.secondary" mb={1.5} px={2} fontWeight="medium">
            Select Node Type
          </Box>

          {/* Divider */}
          <Box borderTopWidth="1px" borderColor="gray.200" my={1.5} />

          {/* Categories */}
          {menuData.categories.map((category) => (
            <Box key={category.name} mb={1}>
              <button
                type="button"
                onClick={() =>
                  setExpandedCategory(expandedCategory === category.name ? null : category.name)
                }
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor:
                    expandedCategory === category.name ? menuActiveBg : 'transparent',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: menuTextColor,
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (expandedCategory !== category.name)
                    e.currentTarget.style.backgroundColor = menuHoverBg
                }}
                onMouseLeave={(e) => {
                  if (expandedCategory !== category.name)
                    e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <span>{category.name}</span>
                {expandedCategory === category.name ? (
                  <ChevronDown size={14} />
                ) : (
                  <ChevronRight size={14} />
                )}
              </button>

              {/* Category nodes */}
              {expandedCategory === category.name && (
                <Box ml={2} mt={1}>
                  {category.nodes.map((node) => (
                    <button
                      key={node.slug}
                      type="button"
                      onClick={() => {
                        onChangeNodeType(id, node)
                        onClose?.()
                      }}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 8px',
                        borderRadius: '4px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        fontSize: '13px',
                        color: menuTextColor,
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = menuHoverBg)}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      {node.logoUrl ? (
                        <img
                          src={node.logoUrl}
                          alt={node.name}
                          style={{ width: 16, height: 16, objectFit: 'contain' }}
                        />
                      ) : (
                        <Box w="16px" h="16px" bg="gray.300" borderRadius="2px" />
                      )}
                      <Text fontSize="sm" truncate maxW="130px">
                        {node.name}
                      </Text>
                    </button>
                  ))}
                </Box>
              )}
            </Box>
          ))}

          {/* Uncategorized nodes */}
          {menuData.uncategorized && menuData.uncategorized.length > 0 && (
            <>
              <Box borderTopWidth="1px" borderColor="gray.200" my={1.5} />
              {menuData.uncategorized.map((node) => (
                <button
                  key={node.slug}
                  type="button"
                  onClick={() => {
                    onChangeNodeType(id, node)
                    onClose?.()
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 8px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: menuTextColor,
                    transition: 'background-color 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = menuHoverBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  {node.logoUrl ? (
                    <img
                      src={node.logoUrl}
                      alt={node.name}
                      style={{ width: 16, height: 16, objectFit: 'contain' }}
                    />
                  ) : (
                    <Box w="16px" h="16px" bg="gray.300" borderRadius="2px" />
                  )}
                  <Text fontSize="sm" truncate maxW="130px">
                    {node.name}
                  </Text>
                </button>
              ))}
            </>
          )}
        </Box>
      )}
    </>
  )
}
